import { forwardRef } from 'react';
import { ScrollView, type ScrollViewProps } from 'react-native';
import Animated, { type AnimatedRef } from 'react-native-reanimated';

import { BOUNDED } from './bounds';
import { cn } from './cn';

export interface BoundedScrollViewProps extends ScrollViewProps {
  /**
   * Render a reanimated `Animated.ScrollView` instead of a plain `ScrollView`,
   * so a `useAnimatedRef` / `scrollTo` worklet can drive it. The ref forwards to
   * the underlying scroll view in both cases.
   */
  animated?: boolean;
  /**
   * Animated ref to the scroll node (implies `animated`), for worklet consumers
   * like `useScrollOffset` — mirrors `BoundedKeyboardAwareScrollView`'s
   * `scrollRef`. Takes the ref slot; combine with the plain forwarded `ref`
   * only if you add merging.
   */
  scrollRef?: AnimatedRef<Animated.ScrollView>;
}

/**
 * `ScrollView` with the global page-width cap baked into its content container:
 * the scroll surface stays full-width (gutters still scroll) while content is
 * capped + centered. Use this instead of hand-applying `BOUNDED` per screen.
 */
export const BoundedScrollView = forwardRef<ScrollView, BoundedScrollViewProps>(
  ({ animated, scrollRef, contentContainerClassName, ...props }, ref) => {
    const className = cn(BOUNDED, contentContainerClassName);
    return animated || scrollRef ? (
      <Animated.ScrollView
        ref={scrollRef ?? ref}
        contentContainerClassName={className}
        {...props}
      />
    ) : (
      <ScrollView ref={ref} contentContainerClassName={className} {...props} />
    );
  },
);

BoundedScrollView.displayName = 'BoundedScrollView';
